import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircleIcon, WarningIcon, ArrowsClockwiseIcon, NotePencilIcon } from '@/components/icons';
import Spinner from '@/components/ui/spinner';
import { useI18n } from '@/i18n/I18nProvider';
import { useGetStudentIdhInfo } from '@/lib/hooks/useGetStudentIdhInfo';
import { useStudentSync } from '@/lib/hooks/useStudentSync';
import { useToastNotifications } from '@/lib/hooks/use-toast-notifications';

interface StudentIdhStatusCardProps {
  studentId: string;
  onCanProceedChange?: (canProceed: boolean) => void;
  onDataSynced?: () => Promise<void> | void;
}

export function StudentIdhStatusCard({
  studentId,
  onCanProceedChange,
  onDataSynced,
}: StudentIdhStatusCardProps) {
  const { t, locale } = useI18n();
  const toast = useToastNotifications();
  const { syncStudents } = useStudentSync();
  const [isSyncing, setIsSyncing] = React.useState(false);

  const {
    idhData,
    statusId: idhStatusId,
    isLoading: isIdhLoading,
  } = useGetStudentIdhInfo(studentId, {
    swrOptions: { dedupingInterval: 0 },
  });

  const canProceedWithIDH = React.useMemo(() => {
    if (isIdhLoading) return false;
    if (!idhData) return false;
    if (idhStatusId === null) return false;
    return idhStatusId === 4;
  }, [idhData, idhStatusId, isIdhLoading]);

  React.useEffect(() => {
    onCanProceedChange?.(canProceedWithIDH);
  }, [canProceedWithIDH, onCanProceedChange]);

  const handleSyncUpdate = React.useCallback(async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      await syncStudents();
      await onDataSynced?.();
      toast.success(
        locale === 'ar' ? 'تم بنجاح' : 'Success',
        locale === 'ar'
          ? 'تم تحديث البيانات بنجاح. يمكنك الآن المتابعة.'
          : 'Data synced successfully. You can now proceed.'
      );
    } catch (error: any) {
      toast.error(
        locale === 'ar' ? 'خطأ' : 'Error',
        error?.message || (locale === 'ar' ? 'فشل تحديث البيانات.' : 'Failed to sync data.')
      );
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, syncStudents, onDataSynced, toast, locale]);

  const idhStatusDetails = React.useMemo(() => {
    // Complete (4)
    if (idhStatusId === 4) {
      return {
        statusText: t.accepted,
        title: locale === 'ar' ? 'تم اعتماد طلب تحديث البيانات' : 'Data Update Request Approved',
        description:
          locale === 'ar'
            ? 'تم تحديث بيانات الطالب بنجاح. يرجى الضغط على زر "مزامنة البيانات" لتأكيد التحديث ثم المتابعة لتوقيع الميثاق.'
            : 'Student data has been successfully updated. Please click "Sync Data" button to confirm the update, then proceed to sign the charter.',
        actionType: 'sync' as const,
        icon: 'check' as const,
        badgeVariant: 'bg-green-200 dark:bg-green-800 text-green-900 dark:text-green-100',
        cardBorder: 'border-green-500 bg-green-50/50 dark:bg-green-900/20',
        titleColor: 'text-green-800 dark:text-green-300',
        descColor: 'text-green-700 dark:text-green-400',
      };
    }

    // In Progress (1), Modified / Resubmitted (3)
    if (idhStatusId === 1 || idhStatusId === 3) {
      return {
        statusText: idhStatusId === 1 ? t.inProgress : t.modified,
        title: locale === 'ar' ? 'طلب تحديث البيانات قيد المراجعة' : 'Data Update Request Under Review',
        description:
          locale === 'ar'
            ? 'تم إرسال طلب لتحديث بيانات الطالب وهو قيد المراجعة حاليًا. لا يمكن توقيع الميثاق حتى يتم اعتماد الطلب ومزامنة البيانات.'
            : 'A request to update student data has been submitted and is currently under review. Charter signing will be available once the request is approved and data is synced.',
        actionType: 'none' as const,
        icon: 'warning' as const,
        badgeVariant: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200 border border-amber-300',
        cardBorder: 'border-amber-500 bg-amber-50/70 dark:bg-amber-950/20',
        titleColor: 'text-amber-800 dark:text-amber-300',
        descColor: 'text-amber-700 dark:text-amber-400',
      };
    }

    // Returned (2) - Needs parent to review comments and resubmit
    if (idhStatusId === 2) {
      return {
        statusText: t.returned,
        title: locale === 'ar' ? 'تمت إعادة طلب تحديث البيانات' : 'Data Update Request Returned',
        description:
          locale === 'ar'
            ? 'تمت إعادة طلب تحديث بيانات الطالب للتعديل. يرجى مراجعة الملاحظات وتحديث البيانات وإعادة التقديم للمتابعة.'
            : 'The student data update request was returned for correction. Please review notes, update the profile, and resubmit to proceed.',
        actionType: 'edit' as const,
        icon: 'warning' as const,
        badgeVariant: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200 border border-amber-300',
        cardBorder: 'border-amber-500 bg-amber-50/70 dark:bg-amber-950/20',
        titleColor: 'text-amber-800 dark:text-amber-300',
        descColor: 'text-amber-700 dark:text-amber-400',
      };
    }

    // Rejected (5)
    if (idhStatusId === 5) {
      return {
        statusText: t.rejected,
        title: locale === 'ar' ? 'تم رفض طلب تحديث البيانات' : 'Data Update Request Rejected',
        description:
          locale === 'ar'
            ? 'تم رفض طلب تحديث بيانات الطالب السابق. يرجى التواصل مع إدارة المدرسة.'
            : 'The previous student data update request was rejected. Please contact the school admin.',
        actionType: 'none' as const,
        icon: 'warning' as const,
        badgeVariant: 'bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200 border border-red-300',
        cardBorder: 'border-red-500 bg-red-50/70 dark:bg-red-950/20',
        titleColor: 'text-red-800 dark:text-red-300',
        descColor: 'text-red-700 dark:text-red-400',
      };
    }

    // Default: No application submitted / null
    return {
      statusText: locale === 'ar' ? 'لم يتم تقديم طلب لتحديث البيانات' : 'No application submitted',
      title: locale === 'ar' ? 'يجب تحديث بيانات الطالب' : 'Student Data Update Required',
      description:
        locale === 'ar'
          ? 'لا يمكن توقيع الميثاق حاليًا. يجب تحديث بيانات الطالب أولاً قبل المتابعة.'
          : 'Charter signing is currently unavailable. Student data must be updated first before proceeding.',
      actionType: 'edit' as const,
      icon: 'warning' as const,
      badgeVariant: 'bg-orange-200 dark:bg-orange-800 text-orange-900 dark:text-orange-100',
      cardBorder: 'border-orange-500 bg-orange-50/70 dark:bg-orange-950/20',
      titleColor: 'text-orange-800 dark:text-orange-300',
      descColor: 'text-orange-700 dark:text-orange-400',
    };
  }, [idhStatusId, locale, t]);

  if (isIdhLoading) {
    return (
      <Card className="border border-blue-200 shadow-sm bg-blue-50/50 dark:bg-blue-900/20">
        <CardContent className="pt-4">
          <div className="mx-auto">
            <Spinner />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-2 shadow-sm ${idhStatusDetails.cardBorder}`}>
      <CardContent className="pt-4">
        <div className="flex items-start gap-3">
          {idhStatusDetails.icon === 'check' ? <CheckCircleIcon /> : <WarningIcon />}
          <div className="flex-1">
            <p className={`font-semibold mb-1 ${idhStatusDetails.titleColor}`}>
              {idhStatusDetails.title}
            </p>
            <p className={`text-sm mb-3 ${idhStatusDetails.descColor}`}>
              {idhStatusDetails.description}
            </p>
            <div className="bg-white dark:bg-slate-900/60 rounded p-3 text-xs border border-border/50">
              <div className={`flex items-center gap-2 ${idhStatusDetails.actionType !== 'none' ? 'mb-3' : ''}`}>
                <span className="font-medium text-foreground">
                  {locale === 'ar' ? 'الحالة الحالية:' : 'Current Status:'}
                </span>
                <span className={`px-2 py-0.5 rounded font-medium ${idhStatusDetails.badgeVariant}`}>
                  {idhStatusDetails.statusText}
                </span>
              </div>

              {idhStatusDetails.actionType === 'sync' && (
                <button
                  type="button"
                  onClick={handleSyncUpdate}
                  disabled={isSyncing}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all shadow-sm hover:shadow-lg text-sm w-full justify-center ${
                    isSyncing
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {isSyncing ? (
                    <>
                      <ArrowsClockwiseIcon className="animate-spin" />
                      {locale === 'ar' ? 'جاري المزامنة...' : 'Syncing...'}
                    </>
                  ) : (
                    <>
                      <ArrowsClockwiseIcon />
                      {locale === 'ar' ? 'مزامنة البيانات' : 'Sync Data'}
                    </>
                  )}
                </button>
              )}

              {idhStatusDetails.actionType === 'edit' && (
                <Link
                  to={studentId ? `/students/${encodeURIComponent(studentId)}/update-info?mode=resubmit` : '/dashboard'}
                  className="aegov-btn btm-sm bg-orange-600 hover:bg-orange-700 w-full"
                >
                  <NotePencilIcon />
                  {locale === 'ar' ? 'تحديث بيانات الطالب' : 'Update Student Profile'}
                </Link>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
